import React from "react";
import { Navigate, RouteObject, useParams, useSearchParams } from "react-router-dom";
import { IconType } from "react-icons";
import { LuBuilding2, LuHandshake, LuLink, LuUsers } from "react-icons/lu";
import { CLINICAS_SEGMENT, clinicaPath } from "./adminPaths";
import { legacyAdminPath } from "./legacyAdminPath";

// Cada seção vira um arquivo à parte: só quem é admin baixa, e só a que abrir.
const MedicosPage = React.lazy(() => import("./medicos/MedicosPage"));
const ClinicasPage = React.lazy(() => import("./clinicas/ClinicasPage"));
const ClinicaDetalhePage = React.lazy(() => import("./clinicas/ClinicaDetalhePage"));
const ConvitesPage = React.lazy(() => import("./convites/ConvitesPage"));
const CortesiasPage = React.lazy(() => import("./cortesias/CortesiasPage"));

export interface AdminSection {
  path: string;
  label: string;
  icon: IconType;
  element: React.ReactNode;
}

/**
 * Registro das seções do admin: a sidebar, as abas do celular e as rotas
 * saem daqui. Adicionar ou remover uma seção = uma linha.
 */
export const ADMIN_SECTIONS: AdminSection[] = [
  { path: "medicos", label: "Médicos", icon: LuUsers, element: <MedicosPage /> },
  { path: CLINICAS_SEGMENT, label: "Clínicas assinantes", icon: LuBuilding2, element: <ClinicasPage /> },
  { path: "links-de-pagamento", label: "Links de pagamento", icon: LuLink, element: <ConvitesPage /> },
  { path: "parcerias", label: "Parcerias", icon: LuHandshake, element: <CortesiasPage /> },
];

// Endereços antigos das seções renomeadas — links salvos continuam funcionando.
const RENOMEADAS: Record<string, string> = {
  clinicas: CLINICAS_SEGMENT,
  convites: "links-de-pagamento",
  cortesias: "parcerias",
};

const AdminIndexRedirect: React.FC = () => {
  const [params] = useSearchParams();
  return <Navigate to={`/admin/${legacyAdminPath(params)}`} replace />;
};

/** /admin/clinicas/:id (endereço antigo) → detalhe no endereço novo. */
const ClinicaAntigaRedirect: React.FC = () => {
  const { id = "" } = useParams();
  return <Navigate to={clinicaPath(id)} replace />;
};

export const adminChildRoutes: RouteObject[] = [
  { index: true, element: <AdminIndexRedirect /> },
  ...ADMIN_SECTIONS.map((section) => ({ path: section.path, element: section.element })),
  // Páginas de detalhe: não aparecem na navegação; a seção-mãe continua marcada.
  { path: `${CLINICAS_SEGMENT}/:id`, element: <ClinicaDetalhePage /> },
  ...Object.entries(RENOMEADAS).map(([antigo, novo]) => ({
    path: antigo,
    element: <Navigate to={`/admin/${novo}`} replace />,
  })),
  { path: "clinicas/:id", element: <ClinicaAntigaRedirect /> },
  { path: "*", element: <Navigate to={`/admin/${ADMIN_SECTIONS[0].path}`} replace /> },
];
